import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Beaker, Check, ChevronLeft, ChevronRight, PawPrint, Plus, Trash2, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { FEATURES } from "@/lib/features";

const STEPS_ALL = ["Contact", "Customer", "Pets", "Shipping", "Preferences", "Compliance"];
// When the Pets feature is disabled we still render the same six underlying components
// (indices 1..6), but Pets (index 3) is skipped by the next/back handlers and hidden
// from the progress bar. `CT_OPTIONS` drops "Pet Owner" so the customer never picks a
// pet-specific account type while the feature is dark.
const STEPS = FEATURES.pets ? STEPS_ALL : STEPS_ALL.filter((s) => s !== "Pets");

const CT_OPTIONS = FEATURES.pets
  ? ["Pet Owner", "Veterinarian", "Veterinary Clinic", "Research Organization", "Distributor", "Other"]
  : ["Veterinarian", "Veterinary Clinic", "Research Organization", "Distributor", "Other"];
const EMPTY_PET = { name: "", species: "Dog", breed: "", age: "", weight: "", gender: "", photo: "", vet_name: "", vet_clinic: "", medical_notes: "", current_supplements: "", current_medications: "" };

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}{required && <span className="text-blue-600 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

export default function Onboarding() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const saveTimer = useRef(null);
  const firstLoad = useRef(true);

  useEffect(() => {
    if (!user) return;
    if (user.onboarding_complete) { navigate("/dashboard", { replace: true }); return; }
    api.get("/onboarding/progress").then((r) => {
      setData(r.data.data);
      setStep(r.data.current_step || 1);
      firstLoad.current = false;
    });
  }, [user, navigate]);

  // Autosave debounced
  useEffect(() => {
    if (firstLoad.current || !data) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch("/onboarding/progress", { current_step: step, data });
      } finally { setSaving(false); }
    }, 800);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [data, step]);

  const set = (path, value) => {
    setData((d) => {
      const clone = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] ??= {};
      cur[parts.at(-1)] = value;
      return clone;
    });
  };

  const addPet = () => setData((d) => ({ ...d, pets: [...(d.pets || []), { ...EMPTY_PET }] }));
  const setPet = (i, k, v) => setData((d) => { const pets = [...d.pets]; pets[i] = { ...pets[i], [k]: v }; return { ...d, pets }; });
  const removePet = (i) => setData((d) => ({ ...d, pets: d.pets.filter((_, x) => x !== i) }));

  const validCurrent = useMemo(() => {
    if (!data) return false;
    if (step === 1) return data.contact?.first_name && data.contact?.last_name && data.contact?.phone;
    if (step === 2) {
      if (!data.customer?.account_type) return false;
      if (data.customer.account_type === "Veterinary Clinic") return !!data.customer.clinic_name;
      if (data.customer.account_type === "Distributor") return !!data.customer.company_name;
      return true;
    }
    if (step === 3) return true; // pets optional
    if (step === 4) return ["full_name", "line1", "city", "state", "postal_code"].every((k) => data.address?.[k]);
    if (step === 5) return true;
    if (step === 6) return data.compliance?.age && data.compliance?.research_only && data.compliance?.terms && data.compliance?.privacy;
    return false;
  }, [data, step]);

  const next = () => setStep((s) => {
    let n = Math.min(6, s + 1);
    if (!FEATURES.pets && n === 3) n = 4; // hop over the Pets step
    return n;
  });
  const back = () => setStep((s) => {
    let n = Math.max(1, s - 1);
    if (!FEATURES.pets && n === 3) n = 2; // hop over the Pets step
    return n;
  });

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/onboarding/complete", data);
      toast.success("Welcome to Peps4Pets. Your account is fully set up.");
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-xs uppercase tracking-widest">Loading onboarding…</div>;

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.webp" alt="" className="h-12 w-12 object-contain"/>
          <div>
            <div className="font-display text-lg font-semibold tracking-tight text-[#0e2f6b]">Peps<span className="text-blue-500">4</span>Pets</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Account Setup · Required</div>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            {saving ? <><Save className="w-3.5 h-3.5 animate-pulse" strokeWidth={1.75}/> Saving…</> : <><Check className="w-3.5 h-3.5 text-blue-600" strokeWidth={2}/> Autosaved</>}
          </div>
        </div>

        <div className="mb-6">
          <div className="eyebrow mb-2">
            {(() => {
              // Progress label reflects the visible step count only.
              const totalVisible = STEPS.length;
              const visibleIdx = (!FEATURES.pets && step > 3) ? step - 1 : step;
              const label = STEPS_ALL[step - 1];
              return `Step ${visibleIdx} of ${totalVisible} · ${label}`;
            })()}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Complete Your Account</h1>
          <p className="mt-2 text-slate-600 text-sm">Before accessing pricing and placing orders, please complete your account profile.</p>
          {user && <p className="mt-2 text-xs font-mono text-blue-700">Verified: {user.email}</p>}
        </div>

        {/* Progress bar */}
        <div className="flex gap-1 mb-6" data-testid="onboarding-progress">
          {STEPS.map((s, i) => {
            const currentVisibleIdx = (!FEATURES.pets && step > 3) ? step - 1 : step;
            return (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${i + 1 <= currentVisibleIdx ? "bg-blue-600" : "bg-slate-200"}`}/>
            );
          })}
        </div>

        <div className="border border-slate-200 rounded-md bg-white p-6 sm:p-8" data-testid={`step-${step}`}>
          {/* STEP 1 CONTACT */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="First name" required><Input data-testid="ob-first-name" value={data.contact.first_name || ""} onChange={(e) => set("contact.first_name", e.target.value)}/></Field>
                <Field label="Last name" required><Input data-testid="ob-last-name" value={data.contact.last_name || ""} onChange={(e) => set("contact.last_name", e.target.value)}/></Field>
                <Field label="Phone number" required><Input data-testid="ob-phone" value={data.contact.phone || ""} onChange={(e) => set("contact.phone", e.target.value)}/></Field>
                <Field label="Email"><Input value={data.contact.email || ""} disabled className="bg-slate-50"/></Field>
                <Field label="Business name (optional)"><Input value={data.contact.business_name || ""} onChange={(e) => set("contact.business_name", e.target.value)}/></Field>
                <Field label="Job title (optional)"><Input value={data.contact.job_title || ""} onChange={(e) => set("contact.job_title", e.target.value)}/></Field>
                <Field label="Preferred contact method">
                  <select value={data.contact.preferred_contact || "Email"} onChange={(e) => set("contact.preferred_contact", e.target.value)} className="h-10 w-full px-3 rounded-md border border-slate-300 text-sm">
                    <option>Email</option><option>Phone</option><option>SMS</option>
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* STEP 2 CUSTOMER */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Customer Information</h2>
              <p className="text-sm text-slate-600">Help us tailor pricing and documentation to your role.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {CT_OPTIONS.map((opt) => (
                  <label key={opt} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${data.customer.account_type === opt ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}>
                    <input type="radio" data-testid={`ob-account-type-${opt.toLowerCase().replace(/\s/g, "-")}`} name="account_type" checked={data.customer.account_type === opt} onChange={() => set("customer.account_type", opt)}/>
                    <span className="text-sm text-slate-900">{opt}</span>
                  </label>
                ))}
              </div>
              {data.customer.account_type === "Veterinary Clinic" && (
                <div className="pt-3 border-t border-slate-100 space-y-3">
                  <Field label="Clinic name" required><Input value={data.customer.clinic_name || ""} onChange={(e) => set("customer.clinic_name", e.target.value)}/></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="License number (optional)"><Input value={data.customer.license_number || ""} onChange={(e) => set("customer.license_number", e.target.value)}/></Field>
                    <Field label="Clinic website"><Input value={data.customer.clinic_website || ""} onChange={(e) => set("customer.clinic_website", e.target.value)}/></Field>
                  </div>
                </div>
              )}
              {data.customer.account_type === "Distributor" && (
                <div className="pt-3 border-t border-slate-100 space-y-3">
                  <Field label="Company name" required><Input value={data.customer.company_name || ""} onChange={(e) => set("customer.company_name", e.target.value)}/></Field>
                  <Field label="Reseller website"><Input value={data.customer.reseller_website || ""} onChange={(e) => set("customer.reseller_website", e.target.value)}/></Field>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 PETS */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-semibold text-slate-900">Pets (optional)</h2>
                  <p className="text-sm text-slate-600 mt-1">Add your pets now — you can always add more later.</p>
                </div>
                <Button data-testid="ob-add-pet" onClick={addPet} variant="outline" className="border-slate-300"><Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Add pet</Button>
              </div>
              {(data.pets || []).length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-md p-8 text-center">
                  <PawPrint className="w-8 h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5}/>
                  <div className="text-sm text-slate-600">No pets added yet. Click "Add pet" or skip to the next step.</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.pets.map((p, i) => (
                    <div key={i} className="border border-slate-200 rounded-md p-4 relative">
                      <button onClick={() => removePet(i)} className="absolute top-3 right-3 p-1 rounded-md hover:bg-red-50" aria-label="remove"><Trash2 className="w-4 h-4 text-red-500" strokeWidth={1.75}/></button>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-blue-700 mb-3">Pet {i+1}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Pet name" required><Input data-testid={`ob-pet-name-${i}`} value={p.name} onChange={(e) => setPet(i, "name", e.target.value)}/></Field>
                        <Field label="Species" required>
                          <select value={p.species} onChange={(e) => setPet(i, "species", e.target.value)} className="h-10 w-full px-3 rounded-md border border-slate-300 text-sm">
                            <option>Dog</option><option>Cat</option><option>Horse</option><option>Other</option>
                          </select>
                        </Field>
                        <Field label="Breed"><Input value={p.breed} onChange={(e) => setPet(i, "breed", e.target.value)}/></Field>
                        <Field label="Age"><Input value={p.age} onChange={(e) => setPet(i, "age", e.target.value)} placeholder="e.g. 4 years"/></Field>
                        <Field label="Weight"><Input value={p.weight} onChange={(e) => setPet(i, "weight", e.target.value)} placeholder="e.g. 22 kg"/></Field>
                        <Field label="Gender"><Input value={p.gender} onChange={(e) => setPet(i, "gender", e.target.value)}/></Field>
                        <Field label="Photo URL (optional)"><Input value={p.photo} onChange={(e) => setPet(i, "photo", e.target.value)}/></Field>
                        <Field label="Veterinarian name"><Input value={p.vet_name} onChange={(e) => setPet(i, "vet_name", e.target.value)}/></Field>
                        <Field label="Veterinary clinic"><Input value={p.vet_clinic} onChange={(e) => setPet(i, "vet_clinic", e.target.value)}/></Field>
                      </div>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Current supplements"><Input value={p.current_supplements} onChange={(e) => setPet(i, "current_supplements", e.target.value)} placeholder="Comma-separated"/></Field>
                        <Field label="Current medications"><Input value={p.current_medications} onChange={(e) => setPet(i, "current_medications", e.target.value)} placeholder="Comma-separated"/></Field>
                      </div>
                      <div className="mt-3">
                        <Field label="Medical notes"><Textarea rows={2} value={p.medical_notes} onChange={(e) => setPet(i, "medical_notes", e.target.value)}/></Field>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4 SHIPPING */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Default Shipping Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Recipient name" required><Input data-testid="ob-ship-name" value={data.address.full_name || ""} onChange={(e) => set("address.full_name", e.target.value)}/></Field>
                <Field label="Company (optional)"><Input value={data.address.company || ""} onChange={(e) => set("address.company", e.target.value)}/></Field>
                <Field label="Phone"><Input value={data.address.phone || ""} onChange={(e) => set("address.phone", e.target.value)}/></Field>
                <Field label="Country"><Input value={data.address.country || "US"} onChange={(e) => set("address.country", e.target.value)}/></Field>
                <div className="md:col-span-2"><Field label="Address line 1" required><Input data-testid="ob-ship-line1" value={data.address.line1 || ""} onChange={(e) => set("address.line1", e.target.value)}/></Field></div>
                <div className="md:col-span-2"><Field label="Address line 2"><Input value={data.address.line2 || ""} onChange={(e) => set("address.line2", e.target.value)}/></Field></div>
                <Field label="City" required><Input data-testid="ob-ship-city" value={data.address.city || ""} onChange={(e) => set("address.city", e.target.value)}/></Field>
                <Field label="State" required><Input data-testid="ob-ship-state" value={data.address.state || ""} onChange={(e) => set("address.state", e.target.value)}/></Field>
                <Field label="Postal code" required><Input data-testid="ob-ship-zip" value={data.address.postal_code || ""} onChange={(e) => set("address.postal_code", e.target.value)}/></Field>
              </div>
            </div>
          )}

          {/* STEP 5 PREFERENCES */}
          {step === 5 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Account Preferences</h2>
              <p className="text-sm text-slate-600">Choose what we email or text you about. Change anytime in profile settings.</p>
              <div className="space-y-2">
                {[
                  ["newsletter", "Newsletter — new batch drops"],
                  ["sms", "SMS notifications"],
                  ["order_updates", "Order updates"],
                  ["research_updates", "Research updates"],
                  ["product_notifications", "New product notifications"],
                  ["educational_articles", "Educational articles"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={!!data.preferences[k]} onChange={(e) => set(`preferences.${k}`, e.target.checked)}/>
                    <span className="text-sm text-slate-900">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* STEP 6 COMPLIANCE */}
          {step === 6 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Compliance Acknowledgements</h2>
              <p className="text-sm text-slate-600">All items are required.</p>
              <div className="space-y-2">
                {[
                  ["age", "I certify I am at least 21 years old."],
                  ["research_only", "I confirm that the chemicals purchased are not for human or animal therapeutic purposes but for research only."],
                  ["terms", "I agree to the Terms of Service."],
                  ["privacy", "I agree to the Privacy Policy."],
                ].map(([k, label]) => (
                  <label key={k} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${data.compliance[k] ? "border-blue-300 bg-blue-50/50" : "border-slate-200 hover:border-slate-400"}`}>
                    <input type="checkbox" data-testid={`ob-compliance-${k}`} checked={!!data.compliance[k]} onChange={(e) => set(`compliance.${k}`, e.target.checked)} className="mt-0.5"/>
                    <span className="text-sm text-slate-900">{label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-600" strokeWidth={1.75}/>
                <span>Your account will be finalized once you complete this step. You'll see pricing and can place orders immediately.</span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
            <Button onClick={back} disabled={step === 1} variant="ghost" className="text-slate-600" data-testid="ob-back">
              <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={1.75}/> Back
            </Button>
            {step < 6 ? (
              <Button onClick={next} disabled={!validCurrent} className="bg-slate-900 hover:bg-blue-600 text-white h-11 px-6 rounded-md transition-colors" data-testid="ob-next">
                Next <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75}/>
              </Button>
            ) : (
              <Button onClick={submit} disabled={!validCurrent || submitting} className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 rounded-md text-base font-medium" data-testid="ob-complete">
                {submitting ? "Finalizing…" : "Complete Account Setup"} <Check className="w-4 h-4 ml-2" strokeWidth={2}/>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
